import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import RankingNudgeBanner from "./RankingNudgeBanner";
import BookmarkMoversBanner from "./artistsTable/BookmarkMoversBanner";
import ArtistRankingModal from "./ArtistRankingModal";
import { bookmarkedAtMillis } from "./artistsTable/artistsTableUtils";

// A signed-in fan's or artist's co-sign nudges, shown at the top of both the
// homepage and the leaderboard: "co-sign N more to start ranking" / "N ready
// to rank" (RankingNudgeBanner), which of their co-signs moved up this week
// (BookmarkMoversBanner), and the ranking modal "Rank Now" opens.
//
// viewer comes from useViewer, ranking from useArtistRankings. The page owns
// the modal's queue (rankingQueue / setRankingQueue) because the leaderboard
// also opens it straight after a new co-sign. showMovers=false drops the
// movers banner, for the homepage, which has its own "Your co-signs this
// week" row.
function CosignNudges({ viewer, ranking, weeklyGainers, rankingQueue, setRankingQueue, showMovers = true }) {
  const navigate = useNavigate();
  const { user, userProfile, bookmarkedArtists } = viewer;
  const { rankings, rankedKeys, refetch, error } = ranking;

  // Co-signed but not yet placed in the viewer's ranking, oldest co-sign first.
  const pendingRankArtists = useMemo(
    () => bookmarkedArtists
      .filter((a) => !rankedKeys.has(a.artistKey))
      .sort((a, b) => bookmarkedAtMillis(a) - bookmarkedAtMillis(b)),
    [bookmarkedArtists, rankedKeys]
  );

  // Co-signing used to be a one-way action - nothing brought a user back to
  // check on it. This surfaces which of their own co-signs moved up this week.
  const bookmarkedMovers = useMemo(() => {
    const keys = new Set(bookmarkedArtists.map((a) => a.artistKey));
    return weeklyGainers.filter((mover) => keys.has(mover.artistKey));
  }, [weeklyGainers, bookmarkedArtists]);

  if (!user || (userProfile?.accountType !== "fan" && userProfile?.accountType !== "artist")) {
    return null;
  }

  const closeModal = () => {
    setRankingQueue(null);
    refetch();
  };

  return (
    <>
      {error && (
        <p role="alert">
          {error}{" "}
          <button type="button" onClick={refetch}>Retry</button>
        </p>
      )}
      <RankingNudgeBanner
        savedCount={bookmarkedArtists.length}
        pendingCount={pendingRankArtists.length}
        onRankNow={() => {
          if (pendingRankArtists.length > 0) setRankingQueue(pendingRankArtists);
        }}
      />
      {showMovers && (
        <BookmarkMoversBanner
          movers={bookmarkedMovers}
          onSelect={(name) => navigate(`/artist/${encodeURIComponent(name)}`)}
        />
      )}
      {rankingQueue && (
        <ArtistRankingModal
          uid={user.uid}
          pendingArtists={rankingQueue}
          existingRankings={rankings}
          onClose={closeModal}
          onComplete={closeModal}
        />
      )}
    </>
  );
}

export default CosignNudges;
